// TILES FLIP OLD→NEW, NEVER THROUGH BLACK. Two things caused the black: the tiles were a fixed size
// that left GAPS, and the flip squashed them to a sliver — so at the moment of change the
// background showed through.
import { useEffect, useMemo, useState } from "react";
import { Settings2, X } from "lucide-react";
import {
  TIMING,
  VARIANTS,
  type AnimCell,
  type AnimVariant,
} from "../lib/mapSoilAnimation";
import { normalizeDesign, ringPath } from "../lib/animGeometry";
import { beatAt, decileColor, decileOf, tileIn } from "../lib/behaviorAnim";
import {
  BEHAVIOR_PLANES,
  forkedPlaneIndex,
  GRID_PLANES,
  planeIndex,
} from "../lib/animPlanes";
import {
  excludedBy,
  GRID_BEATS,
  GRID_CLEAR,
  GRID_DARK,
  gridBeatAt,
  overlayIn,
} from "../lib/gridAnim";
import {
  panelBox,
  panelPlaneIndex,
  panelTileIn,
  RELATIVE_PANELS,
  relativeStateAt,
} from "../lib/relativeAnim";
import { behaviorColor } from "../lib/behaviorZones";
import { zoneColor, zoneNumbersIn } from "../lib/zoneColors";
import { useElapsed, useTilePaint } from "../hooks/useTilePaint";
import type { AnimStatus } from "../hooks/useAnimCells";
import type { Bbox } from "../lib/geo";
import { useSamplingConfirm } from "../stores/samplingConfirm";
import { SamplingConfirmBody } from "../features/sampling-confirm/SamplingConfirmDialog";
import { Modal } from "../ui/Modal";

/* * How long the window will wait for pixels before giving up and saying so. */
const PATIENCE_MS = 8000;

/*
 *
 * Unused shim removed — `useElapsed` and the tile painting now live in `hooks/useTilePaint` shared
 * with the map-view intro so the two surfaces cannot animate differently.
 */
export function Subtitle({ steps }: { steps: readonly string[] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setI((n) => (n + 1) % steps.length),
      TIMING.subtitleMs,
    );
    return () => clearInterval(id);
  }, [steps.length]);
  return (
    <div className="relative h-5 text-center">
      {steps.map((s, n) => (
        <span
          key={s}
          className={`absolute inset-0 text-[13px] text-muted transition-opacity duration-300 ${n === i ? "opacity-100" : "opacity-0"}`}
        >
          {s}
        </span>
      ))}
    </div>
  );
}

export function MapSoilAnimation({
  variant = "map",
  fieldCount,
  cells,
  labels,
  status,
  aspect = 1,
  bbox,
  keys = [],
  ramps = [],
  loop = false,
  onCustomize,
  onClose,
}: {
  /* * which High-Res workflow this is — the model run, or designing a sampling plan */
  variant?: AnimVariant;
  fieldCount: number;
  /* * the first selected field's tiles, normalised to 0..1 in screen space */
  cells: AnimCell[];
  /* * covariate display names, index-aligned with each cell's `colors` */
  labels: string[];
  /* * whether there is anything to draw yet */
  status: AnimStatus;
  /* * the field's true width:height — the map is letterboxed to it so the shape is the user's own */
  aspect?: number;
  /*
   *
   * Without it the sample points are NOT drawn.
   */
  bbox?: Bbox | null;
  /* * the SERVED plane keys and ramps — the Behavior stage addresses planes BY NAME, never by position */
  keys?: string[];
  ramps?: (string[] | null)[];
  /*
   *
   * "Customize" on the points view — the user wants to EDIT this design instead of ordering it.
   */
  onCustomize?: (fc?: GeoJSON.FeatureCollection) => void;
  /*
   *
   * LOOP THE FIRST ACT UNTIL CLOSED — Map-With-Existing → Behavior: "the normal animation window
   * for this should show up (the 3 indexes spreading across the field).
   */
  loop?: boolean;
  onClose: () => void;
}) {
  // The note arrives OVER the map rather than replacing it (correction 1). `gaveUp` is the separate
  // unhappy case: nothing ever loaded, so there is no map to put it over.
  const [noted, setNoted] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const tRaw = useElapsed(status === "ready");
  // looping: the clock wraps at the first act's span, so the three axes cycle forever
  const t = loop ? tRaw % TIMING.sampleMs : tRaw;

  // ACT TWO, sample variant only.
  const pending = useSamplingConfirm((s) => s.pending);
  const showPoints = !loop && variant !== "map" && t >= TIMING.sampleMs;

  // Mounted means hosted; unmounted means released, by construction, including the paths that never
  // call `onClose` at all.
  const setHosted = useSamplingConfirm((s) => s.setHosted);
  const clearConfirm = useSamplingConfirm((s) => s.clear);

  /*
   *
   * The user is going to design their own, so the priced confirmation for the DEFAULT design must
   * not survive — it would otherwise reappear over the editor the moment this unmounts and release
   * hosting asking them to buy the design they just walked away from. The design goes WITH them so
   * the editor opens on the points they were looking at, rather than resolving it a second time.
   */
  const goCustomize = () => {
    const fc = useSamplingConfirm.getState().pending?.preview?.fc;
    clearConfirm();
    onCustomize?.(fc);
  };
  useEffect(() => {
    if (variant === "map") return;
    setHosted(true);
    return () => setHosted(false);
  }, [variant, setHosted]);

  // It also gives up rather than waiting forever: an indefinite "reading layers…" is worse than the
  // blank it replaces.
  useEffect(() => {
    if (status === "loading") {
      const bail = setTimeout(() => setGaveUp(true), PATIENCE_MS);
      return () => clearTimeout(bail);
    }
    if (status === "empty") {
      setGaveUp(true);
      return;
    }
    if (variant !== "map" && !loop) return;
    const id = setTimeout(() => setNoted(true), TIMING.mapMs);
    return () => clearTimeout(id);
  }, [status, variant, loop]);

  const v = VARIANTS[variant];
  // The viewBox is stretched to the field's aspect and `preserveAspectRatio` letterboxes it
  // replacing the old `none` that scaled every field to 4:3 — which is exactly the distortion the
  // aspect-matched raster exists to avoid.
  const { vh, size, painted, white, caption } = useTilePaint({
    cells,
    labels,
    aspect,
    t,
    scanner: v.scanner,
  });

  /*
   *
   * BEHAVIOR-CLASSIFIED ZONES: three axes, each spiralling on as a percentile and then settling
   * into its deciles, all inside the five seconds before the zones and the price arrive.
   */
  const behavior = useMemo(() => {
    if (variant !== "behavior" || !cells.length) return null;
    const idx = {
      moisture: forkedPlaneIndex(keys, cells, BEHAVIOR_PLANES.moisture),
      productivity: forkedPlaneIndex(keys, cells, BEHAVIOR_PLANES.productivity),
      fertility: forkedPlaneIndex(keys, cells, BEHAVIOR_PLANES.fertility),
    };
    if (idx.moisture < 0 || idx.productivity < 0 || idx.fertility < 0)
      return null;

    const beat = beatAt(t, TIMING.sampleMs);
    const at = idx[beat.axis];
    const stops = ramps[at] ?? null;
    const tiles = cells.map((c) => {
      const v = c.values[at];
      return {
        c,
        // the percentile's own colour, from the plane the user's zones were actually built on
        base: c.colors[at] ?? null,
        // the decile is DERIVED (`floor(pct/10)`, top bin closed) and coloured from the SAME ramp
        // compute deliberately does not ship a decile plane, because a third copy of that bin edge
        // would be the one nobody tests.
        decile: v == null ? null : decileColor(stops, decileOf(v)),
        shown: beat.showDecile || tileIn(c, beat.within),
      };
    });
    return { tiles, beat, label: labels[at] ?? "" };
  }, [variant, cells, keys, ramps, labels, t]);

  /*
   *
   * A cell the plane has no answer for stays neutral rather than being painted available, because
   * "available" is a claim about where a sample could go.
   */
  const grid = useMemo(() => {
    if (variant !== "grid" || !cells.length) return null;
    const at = planeIndex(keys, GRID_PLANES.exclusion.key);
    if (at < 0) return null;
    const beat = gridBeatAt(t, TIMING.sampleMs);
    const tiles = cells.map((c) => {
      const ex = excludedBy(c.values[at], beat.mask);
      // what this tile looked like BEFORE this beat's rule joined — the state it holds until the sweep
      // arrives, so the green only ever shrinks
      const before = excludedBy(
        c.values[at],
        beat.mask & ~GRID_BEATS[beat.index]!.bits,
      );
      return {
        c,
        priorColor:
          before == null ? "#334155" : before ? GRID_DARK : GRID_CLEAR,
        // the sweep reaches each tile on the same spiral every other animation here uses
        reached: overlayIn(c, beat.within),
        color: ex == null ? "#334155" : ex ? GRID_DARK : GRID_CLEAR,
      };
    });
    return { tiles, beat };
  }, [variant, cells, keys, t]);

  /*
   *
   * MULTI-INPUT, RELATIVE ZONES: the field duplicates — one panel, then two side by side, then a
   * triangle of three — each panel revealing one of the inputs the zones are built from.
   */
  const relative = useMemo(() => {
    if (variant !== "relative" || !cells.length) return null;
    const st = relativeStateAt(t, TIMING.sampleMs);
    const panels = [];
    for (let i = 0; i < st.count; i++) {
      const at = panelPlaneIndex(keys, i);
      if (at < 0) continue;
      panels.push({
        at,
        label: RELATIVE_PANELS[i]!.label,
        box: panelBox(i, st.count, vh),
        // panels already revealed stay fully painted; only the newest one sweeps on
        revealing: i === st.revealing ? st.within : 1,
      });
    }
    return panels.length ? { panels, st } : null;
  }, [variant, cells, keys, vh, t]);

  /*
   *
   * Zone fill, matching the preset card exactly: behaviour zones use `behaviorColor` keyed on the zone
   * number, every other school the shared palette sized to the zones actually present.
   */
  const zoneNumbers = useMemo(
    () =>
      zoneNumbersIn({
        type: "FeatureCollection",
        features: pending?.preview?.fc?.features ?? [],
      } as GeoJSON.FeatureCollection),
    [pending],
  );
  const zoneFill = (zone: number | null, fallbackIndex: number): string => {
    if (variant === "behavior")
      return zone == null ? "#888888" : behaviorColor(zone);
    const at =
      zone == null ? fallbackIndex : Math.max(0, zoneNumbers.indexOf(zone));
    return zoneColor(at, Math.max(1, zoneNumbers.length));
  };

  // PLACED AGAINST THE BOUNDARY'S BBOX — THE SAME MEASUREMENT THE PREVIEW USES, and it refuses
  // rather than guessing.
  const design = useMemo(
    () => normalizeDesign(pending?.preview?.fc, bbox),
    [pending, bbox],
  );

  return (
    <Modal>
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-black/60 p-4">
        <div className="relative flex w-full max-w-lg flex-col gap-2 rounded-lg bg-surface p-4 text-fg shadow-2xl">
          {/* Closes the WINDOW only. */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-2 top-2 rounded p-1 text-muted hover:bg-hover hover:text-fg"
          >
            <X size={16} />
          </button>

          <h2 className="pr-6 text-center text-[15px] font-semibold">
            {v.title(fieldCount)}
          </h2>
          <div
            className={`transition-opacity duration-700 ${showPoints ? "pointer-events-none opacity-0" : "opacity-100"}`}
          >
            <Subtitle steps={v.subtitles} />
          </div>

          <div className="relative mt-1 aspect-[4/3] w-full overflow-hidden rounded bg-canvas/60">
            {/* SAYS SO rather than showing an empty rectangle — a blank box reads as broken. */}
            {status === "loading" && !gaveUp && (
              <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-faint">
                Reading this field’s layers…
              </div>
            )}

            {/* THE FIELD'S SILHOUETTE, AND IT NEVER FADES. */}
            {/* This layer is a FULL-SIZE field, and the relative variant shrinks and multiplies its panels, so it stayed behind them as a fourth boundary that never moved. */}
            {status === "ready" && !relative && (
              <div className="absolute inset-0">
                <svg
                  viewBox={`0 0 100 ${vh}`}
                  preserveAspectRatio="xMidYMid meet"
                  className="h-full w-full"
                >
                  {cells.map((c, i) => (
                    <rect
                      key={i}
                      x={c.x * 100 - size / 2}
                      y={c.y * vh - size / 2}
                      width={size}
                      height={size}
                      fill="#334155"
                    />
                  ))}
                </svg>
              </div>
            )}

            {/* THE COVARIATE TILES — they LOOP and fade only when the points take over. */}
            {status === "ready" && (
              <div
                className="absolute inset-0 transition-opacity ease-out"
                style={{
                  opacity: showPoints ? 0 : 1,
                  transitionDuration: `${TIMING.fadeMs}ms`,
                }}
              >
                <svg
                  viewBox={`0 0 100 ${vh}`}
                  preserveAspectRatio="xMidYMid meet"
                  className="h-full w-full"
                >
                  {behavior?.tiles.map(({ c, base, decile, shown }, i) => (
                    <g key={`b${i}`} opacity={shown ? 1 : 0}>
                      <rect
                        x={c.x * 100 - size / 2}
                        y={c.y * vh - size / 2}
                        width={size}
                        height={size}
                        fill={base ?? "#334155"}
                      />
                      {/* the deciles fade IN over the percentile rather than replacing it outright */}
                      {decile && (
                        <rect
                          x={c.x * 100 - size / 2}
                          y={c.y * vh - size / 2}
                          width={size}
                          height={size}
                          fill={decile}
                          opacity={
                            behavior.beat.showDecile ? behavior.beat.within : 0
                          }
                        />
                      )}
                    </g>
                  ))}
                  {grid?.tiles.map(({ c, color, reached, priorColor }, i) => (
                    <rect
                      key={`g${i}`}
                      x={c.x * 100 - size / 2}
                      y={c.y * vh - size / 2}
                      width={size}
                      height={size}
                      // UNTIL THE SWEEP REACHES IT, A TILE HOLDS THE PREVIOUS BEAT'S STATE — never
                      // green *"shouldn't go back to full green in between the layers"*.
                      fill={reached ? color : priorColor}
                    />
                  ))}
                  {relative?.panels.map((p, pi) => (
                    <g
                      key={`p${pi}`}
                      transform={`translate(${p.box.tx} ${p.box.ty}) scale(${p.box.scale})`}
                    >
                      {cells.map((c, i) => (
                        <rect
                          key={i}
                          x={c.x * 100 - size / 2}
                          y={c.y * vh - size / 2}
                          width={size}
                          height={size}
                          fill={
                            panelTileIn(c, p.revealing)
                              ? (c.colors[p.at] ?? "#334155")
                              : "#334155"
                          }
                        />
                      ))}
                      {/* each boundary gets labelled with its layer name. */}
                      <text
                        x={50}
                        y={vh - 1}
                        textAnchor="middle"
                        fontSize={Math.max(3, 4 / p.box.scale)}
                        fill="#ffffff"
                        stroke="#0f172a"
                        strokeWidth={Math.max(0.6, 0.9 / p.box.scale)}
                        paintOrder="stroke"
                      >
                        {p.label}
                      </text>
                    </g>
                  ))}
                  {!behavior &&
                    !grid &&
                    !relative &&
                    painted.map(({ c, color, scale }, i) => (
                      <rect
                        key={i}
                        x={c.x * 100 - size / 2}
                        y={c.y * vh - size / 2}
                        width={size}
                        height={size}
                        fill={white?.has(i) ? "#ffffff" : color}
                        // `transform-box: fill-box` makes the percentage origin resolve against the rect
                        // itself rather than the SVG viewport — without it the squash throws tiles across
                        // the canvas instead of turning them in place.
                        style={{
                          transformBox: "fill-box",
                          transformOrigin: "center",
                          transform: `scaleY(${scale})`,
                        }}
                      />
                    ))}
                </svg>
                {behavior && (
                  <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
                    <span
                      key={behavior.label}
                      className="rounded bg-black/70 px-2 py-1 text-[12px] font-medium text-white shadow"
                    >
                      {behavior.label}
                    </span>
                  </div>
                )}
                {grid && (
                  <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
                    <span
                      key={grid.beat.label}
                      className="rounded bg-black/70 px-2 py-1 text-[12px] font-medium text-white shadow"
                    >
                      {grid.beat.label}
                    </span>
                  </div>
                )}
                {!behavior && !grid && !relative && caption && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span
                      key={caption}
                      className="rounded bg-black/70 px-2 py-1 text-[12px] font-medium text-white shadow"
                    >
                      {caption}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* THE POINTS. Fade in as the tiles fade out, inside the same letterboxed space. */}
            {showPoints && (
              <div
                className="absolute inset-0 transition-opacity ease-in"
                style={{ opacity: 1, transitionDuration: `${TIMING.fadeMs}ms` }}
              >
                <svg
                  viewBox={`0 0 100 ${vh}`}
                  preserveAspectRatio="xMidYMid meet"
                  className="h-full w-full"
                >
                  {/* ZONES first, so points drawn over a zone design still read */}
                  {design?.rings.map((r, i) => (
                    <path
                      key={`r${i}`}
                      d={ringPath(r.pts, vh)}
                      // It coloured by feature INDEX from a different palette so the animation showed
                      // the user their zones in colours the map would never use.
                      fill={zoneFill(r.zone, i)}
                      fillOpacity={0.75}
                      stroke="#0f172a"
                      strokeWidth={0.3}
                    />
                  ))}
                  {design?.points.map((p, i) => (
                    <circle
                      key={`p${i}`}
                      cx={p.x * 100}
                      cy={p.y * vh}
                      r={Math.max(1, size * 0.6)}
                      fill="#ffffff"
                      stroke="#0f172a"
                      strokeWidth={0.3}
                    />
                  ))}
                </svg>
                {/*
 Customize — same affordance and same corner as the preset card's, because it goes to
                  the same place. Shown on the points view only: there is nothing to customize before it.
*/}
                {onCustomize && (
                  <button
                    className="absolute bottom-2 right-2 z-scrim flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas shadow hover:bg-accent-hover"
                    onClick={goCustomize}
                    title="Edit these points before ordering"
                  >
                    <Settings2 size={11} /> Customize
                  </button>
                )}

                {/*
 No boundary bbox, or a design with no drawable geometry. Say so over the silhouette
                  rather than leaving a bare field, which reads as "it found none".
*/}
                {!design && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded bg-black/70 px-2 py-1 text-[11px] text-white">
                      Points ready — see below
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* The note FADES IN OVER the running map (correction 1), or stands alone when nothing loaded. */}
            {(noted || gaveUp) && (
              <div
                className="pointer-events-none absolute inset-0 flex items-end justify-center pb-3 transition-opacity duration-700"
                style={{ opacity: 1 }}
              >
                <span className="rounded bg-black/75 px-3 py-1.5 text-center text-[12px] text-white shadow">
                  {loop
                    ? "Running in the background — you can hit X and it will keep going."
                    : "Running in background, please check back later."}
                </span>
              </div>
            )}
          </div>

          {showPoints && (
            <div className="mt-1 flex flex-col gap-2">
              <h3 className="text-center text-[14px] font-semibold">
                {v.kind === "zones" ? "Zones" : "Points"} Identified for{" "}
                {fieldCount} Field{fieldCount === 1 ? "" : "s"}
              </h3>
              {pending ? (
                <div className="rounded-lg border border-line">
                  <SamplingConfirmBody />
                </div>
              ) : (
                <p className="text-center text-[12px] text-muted">
                  Working out the sampling plan…
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
